import FileSaver from 'file-saver';
import * as R from 'ramda';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Functional } from '@wayfinder/vms-base';
import { LandscapeState } from '@wayfinder/vms-state';
import * as VmsState from '../backend/index-vms-state';
import { readUploadedFiles } from '../browser/browser-upload';
import { Button, CommandLineTools, SpritesheetInput } from '../components';

// ---------------------------------------------------------------------------------------------------------------------
//
// Utilities
//
// ---------------------------------------------------------------------------------------------------------------------
/*
 * Call reader to read one or more files uploaded by the user and call processor on uploaded data
 * Impure: uses the browser's internal upload mechanism
 * @sig processUploadedFiles :: (Reader, Processor) -> Event -> Promise *
 *   Reader = File -> Promise *|Error
 *   Processor = a -> b
 */
const processUploadedFiles = async (processor, event) => {
    const uploads = await readUploadedFiles(event);
    return R.map(processor, uploads);
};

// ---------------------------------------------------------------------------------------------------------------------
//
// Run tools
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Canonicalize the badges and logos in an AI JSON file by calling canonicalizeBadgesAndLogos.
 * The file to fix was uploaded via the user's browser via an <input type="file"> HTML element
 * Impure: uploads and downloads data via the browser
 * @sig fixBadgesAndLogos :: Event -> Promise <downloaded-file>
 */
// const fixBadgesAndLogos = async (event, badgeScale) => {
//     badgeScale = R.defaultTo(0.17, Number.parseFloat(badgeScale));

//     const canonicalizeBadgesAndLogos = text => AiJson.canonicalizeBadgesAndLogos(JSON.parse(text), badgeScale);

//     const fileName = R.path(['target', 'files', 0, 'name'], event);
//     const results = await processUploadedFiles(canonicalizeBadgesAndLogos, event);
//     const aiJson = results[0];

//     const blob = new Blob([JSON.stringify(aiJson)], { type: 'text/plain;charset=utf-8' });
//     FileSaver.saveAs(blob, fileName);
// };

const parseAiJson = async event => {
    const parseJson = text => JSON.parse(text);

    const fileName = R.path(['target', 'files', 0, 'name'], event);
    const results = await processUploadedFiles(parseJson, event);
    const aiJson = results[0];

    return { fileName, aiJson };
};

/*
 * Pretty-print an AI JSON file with Functional.stringify
 * The file to modify was uploaded via the user's browser via an <input type="file"> HTML element
 * Impure: uploads via the browser and writes errors to the console
 * @sig prettyPrintAiJson :: Event -> Promise ()
 */
const prettyPrintAiJson = async event => {
    const { fileName, aiJson } = await parseAiJson(event);

    const blob = new Blob([Functional.stringify(aiJson)], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, fileName);
};

// const v6ifyAiJson = async (event, venueId) => {
//     const { fileName, aiJson } = await parseAiJson(event);

//     let objects = {
//         aiLayers: [],
//         entities: [],
//         features: [],
//         navigationLines: [],
//         occupancyHistories: [],
//         topLevelAiLayers: [],
//     };

//     try {
//         AiJsonToObjectStore.v6ify(aiJson);

//         objects = AiJsonToObjectStore.toObjectStore(venueId, aiJson, objects);
//         const fromOs = AiJsonFromObjectStore.fromObjectStore(objects);

//         const blob = new Blob([Functional.stringify(fromOs)], { type: 'text/plain;charset=utf-8' });
//         FileSaver.saveAs(blob, fileName.replace('v5', 'v6'));
//     } catch (e) {
//         console.error(e);
//     }
// };

class CommandLineToolsView extends Component {
    generateSpritesheet = async () => {
        const { dispatch } = this.props;
        await dispatch(VmsState.SpritesheetReducer.Thunks.generateSpritesheet());
    };

    onCategoryChange = venueCategory => {
        const { dispatch } = this.props;
        dispatch(VmsState.SpritesheetReducer.Actions.setVenueCategory(venueCategory));
    };

    onVenueChange = venueId => {
        const { dispatch } = this.props;
        dispatch(VmsState.SpritesheetReducer.Actions.setVenue(venueId));
    };

    onAccountChange = accountId => {
        const { dispatch } = this.props;
        dispatch(VmsState.SpritesheetReducer.Actions.setAccount(accountId));
    };

    render = () => {
        const { venueCategory, venueId, accountId, errors, spritesheetBanner, isGeneratingSpritesheet, venueDropdown } = this.props;
        return (
            <div>
                <div style={{ margin: 30 }}>
                    <Button label="Back to App" onClick={() => this.props.dispatch({ type: 'VENUES_ROUTE' })} />
                </div>
                <SpritesheetInput
                    venueCategory={venueCategory}
                    venueId={venueId}
                    accountId={accountId}
                    onCategoryChange={this.onCategoryChange}
                    onVenueChange={this.onVenueChange}
                    onAccountChange={this.onAccountChange}
                    generateSpritesheet={this.generateSpritesheet}
                    errors={errors}
                    loading={isGeneratingSpritesheet}
                    banner={spritesheetBanner}
                    venueDropdown={venueDropdown}
                />
                <CommandLineTools {...this.props} />
            </div>
        );
    };
}

const mapStateToProps = state => {
    const { venueCategory, venueId, accountId } = VmsState.SpritesheetReducer.Selectors.getSpecificity(state);
    const errors = VmsState.SpritesheetReducer.Selectors.getErrors(state) || {};
    const isGeneratingSpritesheet = VmsState.SpritesheetReducer.Selectors.getIsGeneratingSpritesheet(state);
    const spritesheetBanner = VmsState.SpritesheetReducer.Selectors.getSpritesheetMessageBanner(state) || {};
    const venueDropdown = LandscapeState.selectors.getVenueDropdowns(state) || [];
    return {
        onFixBadgesAndLogos: () => {},
        onPrettyPrintAiJson: prettyPrintAiJson,
        onV6ifyAiJson: () => {},
        venueCategory,
        venueId,
        accountId,
        errors,
        isGeneratingSpritesheet,
        spritesheetBanner,
        venueDropdown,
    };
};

export default connect(mapStateToProps)(CommandLineToolsView);
